import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Receipt,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Clock,
  ArrowRight,
  Building2,
  Calendar,
  ChevronDown,
  ChevronRight,
  FileText,
  Loader2,
  Info,
  CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';

// ── helpers ───────────────────────────────────────────────────────────────────

const fmt = (n) =>
  '€' + Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const daysSince = (iso) => {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso + 'T12:00:00')) / 86400000);
  return diff;
};

const PaymentBadge = ({ status }) => {
  const map = {
    DEPOSIT_DUE:   { label: 'Deposit due',   cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    DEPOSIT_PAID:  { label: 'Deposit paid',  cls: 'bg-blue-100 text-blue-700 border-blue-200' },
    BALANCE_DUE:   { label: 'Balance due',   cls: 'bg-orange-100 text-orange-700 border-orange-200' },
    BALANCE_PAID:  { label: 'Balance paid',  cls: 'bg-green-100 text-green-700 border-green-200' },
    REFUNDED:      { label: 'Refunded',      cls: 'bg-gray-100 text-gray-600 border-gray-200' },
  };
  const { label, cls } = map[status] || { label: status, cls: 'bg-gray-100 text-gray-600 border-gray-200' };
  return (
    <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
      {label}
    </span>
  );
};

const InvoiceBadge = ({ invoice }) => {
  if (!invoice) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-red-50 text-red-600 border-red-200">
        <AlertCircle className="w-3 h-3" /> No invoice
      </span>
    );
  }
  const map = {
    GENERATED: { label: 'Generated', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    SENT:      { label: 'Sent',      cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    PAID:      { label: 'Paid',      cls: 'bg-green-50 text-green-700 border-green-200' },
    PAID_OUT:  { label: 'Paid out',  cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    CANCELLED: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-500 border-gray-200' },
  };
  const { label, cls } = map[invoice.status] || { label: invoice.status, cls: 'bg-gray-100 text-gray-500 border-gray-200' };
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
      <CheckCircle2 className="w-3 h-3" /> {label}
    </span>
  );
};

// ── Short-term row ────────────────────────────────────────────────────────────

const ShortTermRow = ({ item }) => {
  const navigate = useNavigate();
  const overdue = daysSince(item.end_date);

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3.5 rounded-xl border transition-colors cursor-pointer hover:bg-gray-50 ${
        item.invoice_pending ? 'border-orange-200 bg-orange-50/40' : 'border-gray-100 bg-white'
      }`}
      onClick={() => navigate(`/bookings/${item.booking_id}`)}
    >
      {/* Left: urgency stripe */}
      <div className={`hidden sm:block w-1 self-stretch rounded-full flex-shrink-0 ${item.invoice_pending ? 'bg-orange-400' : 'bg-green-400'}`} />

      {/* Booking info */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="font-semibold text-gray-900 text-sm">{item.reference}</span>
          <PaymentBadge status={item.payment_status} />
          <InvoiceBadge invoice={item.invoice} />
        </div>
        <p className="text-xs text-gray-500 truncate flex items-center gap-1">
          <Building2 className="w-3 h-3 flex-shrink-0" />
          {item.property_title || item.property_id}
        </p>
      </div>

      {/* Dates */}
      <div className="flex items-center gap-4 text-xs text-gray-500 flex-shrink-0">
        <div className="text-right">
          <p className="font-medium text-gray-700">{fmtDate(item.start_date)}</p>
          <p className="text-gray-400">Check-in</p>
        </div>
        <ArrowRight className="w-3.5 h-3.5 text-gray-300" />
        <div className="text-right">
          <p className="font-medium text-gray-700">{fmtDate(item.end_date)}</p>
          <p className="text-gray-400">Check-out</p>
        </div>
        <div className="text-right">
          <p className="font-medium text-gray-700">{item.nights}n</p>
          <p className="text-gray-400">Nights</p>
        </div>
      </div>

      {/* Amount */}
      <div className="flex-shrink-0 text-right">
        <p className="font-semibold text-gray-900">{fmt(item.total_amount)}</p>
        {overdue !== null && (
          <p className={`text-[11px] ${overdue > 7 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
            {overdue === 0 ? 'Checked out today' : `${overdue}d ago`}
          </p>
        )}
      </div>

      <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
    </div>
  );
};

// ── Long-term row ─────────────────────────────────────────────────────────────

const LongTermRow = ({ item }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`rounded-xl border overflow-hidden ${item.pending_months > 0 ? 'border-amber-200' : 'border-gray-100'}`}>
      {/* Header row */}
      <div
        className={`flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3.5 cursor-pointer transition-colors ${
          item.pending_months > 0 ? 'bg-amber-50/60 hover:bg-amber-50' : 'bg-white hover:bg-gray-50'
        }`}
        onClick={() => setExpanded((e) => !e)}
      >
        <div className={`hidden sm:block w-1 self-stretch rounded-full flex-shrink-0 ${item.pending_months > 0 ? 'bg-amber-400' : 'bg-green-400'}`} />

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-semibold text-gray-900 text-sm">{item.reference}</span>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-purple-50 text-purple-700 border-purple-200">
              Long-term · {item.nights}n
            </span>
            <PaymentBadge status={item.payment_status} />
            {item.pending_months > 0 && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border bg-orange-50 text-orange-700 border-orange-200 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {item.pending_months} month{item.pending_months !== 1 ? 's' : ''} pending
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate flex items-center gap-1">
            <Building2 className="w-3 h-3 flex-shrink-0" />
            {item.property_title || item.property_id}
          </p>
        </div>

        <div className="flex items-center gap-4 text-xs text-gray-500 flex-shrink-0">
          <div className="text-right">
            <p className="font-medium text-gray-700">{fmtDate(item.start_date)}</p>
            <p className="text-gray-400">Check-in</p>
          </div>
          <ArrowRight className="w-3.5 h-3.5 text-gray-300" />
          <div className="text-right">
            <p className="font-medium text-gray-700">{fmtDate(item.end_date)}</p>
            <p className="text-gray-400">Check-out</p>
          </div>
          <div className="text-right">
            <p className="font-semibold text-gray-900">{fmt(item.total_amount)}</p>
            <p className="text-gray-400">Total</p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-7 px-3"
            onClick={(e) => { e.stopPropagation(); navigate(`/bookings/${item.booking_id}`); }}
          >
            Open booking
          </Button>
          {expanded
            ? <ChevronDown className="w-4 h-4 text-gray-400" />
            : <ChevronRight className="w-4 h-4 text-gray-400" />
          }
        </div>
      </div>

      {/* Expanded months */}
      {expanded && (
        <div className="border-t border-gray-100 divide-y divide-gray-50">
          {item.months.map((m) => (
            <div
              key={m.month_number}
              className={`flex flex-wrap items-center gap-3 px-6 py-3 text-sm ${
                m.invoice_pending ? 'bg-red-50/40' : 'bg-white'
              }`}
            >
              <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-gray-600">{m.month_number}</span>
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-gray-700 font-medium">Month {m.month_number}</span>
                <span className="text-gray-400 text-xs ml-2">
                  {fmtDate(m.start_date)} → {fmtDate(m.end_date)} · {m.nights} nights
                </span>
              </div>
              <InvoiceBadge invoice={m.invoice} />
              {m.invoice && (
                <span className="text-xs text-gray-400">{m.invoice.invoice_number}</span>
              )}
              {m.invoice_pending && (
                <span className="text-xs text-red-500 font-medium flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Invoice required
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Summary stat card ─────────────────────────────────────────────────────────

const StatCard = ({ icon: Icon, label, value, sub, color = 'blue' }) => {
  const colors = {
    blue:   'bg-blue-50 text-blue-600',
    orange: 'bg-orange-50 text-orange-600',
    green:  'bg-green-50 text-green-600',
    amber:  'bg-amber-50 text-amber-600',
    red:    'bg-red-50 text-red-600',
  };
  return (
    <Card className="border-gray-100">
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${colors[color]}`}>
            <Icon className="w-4.5 h-4.5" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
            <p className="text-xs font-medium text-gray-500 mt-0.5">{label}</p>
            {sub && <p className="text-[11px] text-gray-400 mt-0.5">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const BillingPage = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('short');    // 'short' | 'long'
  const [showAll, setShowAll] = useState(false);       // show invoiced too

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/billing/pending`);
      setData(res.data);
    } catch {
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data) return null;

  const { short_term, long_term } = data;

  // Filter lists based on showAll toggle
  const shortItems = showAll
    ? short_term.items
    : short_term.items.filter((b) => b.invoice_pending);

  const longItems = showAll
    ? long_term.items
    : long_term.items.filter((b) => b.pending_months > 0);

  return (
    <div className="space-y-6 max-w-5xl">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Receipt className="w-6 h-6 text-[#194a50]" />
            Billing
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Bookings pending invoice generation · as of {fmtDate(data.generated_at)}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          className="flex items-center gap-1.5"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard
          icon={AlertCircle}
          label="Short-term pending"
          value={short_term.pending_count}
          sub="Need owner invoice"
          color={short_term.pending_count > 0 ? 'orange' : 'green'}
        />
        <StatCard
          icon={FileText}
          label="Short-term total"
          value={short_term.count}
          sub="Checked-out bookings"
          color="blue"
        />
        <StatCard
          icon={AlertCircle}
          label="Monthly pending"
          value={long_term.pending_months}
          sub={`Across ${long_term.count} long-term booking${long_term.count !== 1 ? 's' : ''}`}
          color={long_term.pending_months > 0 ? 'amber' : 'green'}
        />
        <StatCard
          icon={CalendarDays}
          label="Active long-term"
          value={long_term.count}
          sub="Bookings with elapsed months"
          color="blue"
        />
      </div>

      {/* Info note */}
      <div className="flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>
          <strong>Short-term</strong> bookings appear here after check-out until an owner invoice is generated.{' '}
          <strong>Long-term</strong> (60+ night) bookings appear for each calendar month that has elapsed without an invoice.
          Open the booking to generate the invoice.
        </span>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b border-gray-200 pb-0">
        <button
          onClick={() => setTab('short')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'short'
              ? 'border-[#194a50] text-[#194a50]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Short-term
          {short_term.pending_count > 0 && (
            <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold">
              {short_term.pending_count}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('long')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'long'
              ? 'border-[#194a50] text-[#194a50]'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Long-term / Monthly
          {long_term.pending_months > 0 && (
            <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold">
              {long_term.pending_months}
            </span>
          )}
        </button>

        {/* Spacer + show-all toggle */}
        <div className="ml-auto pb-2">
          <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#194a50]"
            />
            Show invoiced too
          </label>
        </div>
      </div>

      {/* ── Short-term list ─────────────────────────────────────────────────── */}
      {tab === 'short' && (
        <div className="space-y-2">
          {shortItems.length === 0 ? (
            <div className="text-center py-14">
              <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">
                {showAll ? 'No checked-out bookings' : 'All checked-out bookings are invoiced'}
              </p>
              {!showAll && short_term.count > 0 && (
                <button
                  onClick={() => setShowAll(true)}
                  className="text-xs text-[#194a50] hover:underline mt-1"
                >
                  Show {short_term.count} invoiced booking{short_term.count !== 1 ? 's' : ''}
                </button>
              )}
            </div>
          ) : (
            shortItems.map((item) => (
              <ShortTermRow key={item.booking_id} item={item} />
            ))
          )}
        </div>
      )}

      {/* ── Long-term list ──────────────────────────────────────────────────── */}
      {tab === 'long' && (
        <div className="space-y-2">
          {longItems.length === 0 ? (
            <div className="text-center py-14">
              <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">
                {showAll ? 'No active long-term bookings' : 'All monthly invoices are up to date'}
              </p>
              {!showAll && long_term.count > 0 && (
                <button
                  onClick={() => setShowAll(true)}
                  className="text-xs text-[#194a50] hover:underline mt-1"
                >
                  Show {long_term.count} fully-invoiced booking{long_term.count !== 1 ? 's' : ''}
                </button>
              )}
            </div>
          ) : (
            longItems.map((item) => (
              <LongTermRow key={item.booking_id} item={item} />
            ))
          )}
        </div>
      )}

    </div>
  );
};

export default BillingPage;
